/**
 * Growth — Referral performance.
 * Sources `/api/bi/growth/referrals/` and code-level detail endpoints.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Gift, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { DataView } from '@/components/data/DataView';
import { KPICard } from '@/components/charts/KPICard';
import {
  useReferralDetail,
  useReferralPerformance,
  type GrowthOrderDrillRow,
  type GrowthStoreDrillRow,
  type ReferralDetailCustomerRow,
  type ReferralRow,
} from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

export function ReferralsPage() {
  return (
    <>
      <PageHeader
        eyebrow="Growth"
        title="Referrals"
        description="Referral claims, conversion, CLV, and estimated free-delivery incentive CAC."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/growth">
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Link>
          </Button>
        }
      />
      <ReferralsAnalyticsBody />
    </>
  );
}

export function ReferralsAnalyticsBody({
  showKpis = true,
  detailReturnTo = '/growth/referrals',
}: {
  showKpis?: boolean;
  detailReturnTo?: string;
}) {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );
  const filters = { start: startISO, end: endISO, storeIds };

  const query = useReferralPerformance({ ...filters, limit: 50 });
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const detail = useReferralDetail(selectedId, filters);
  const selectedReferral = query.data?.referrals.find((r) => r.refer_code_id === selectedId) ?? null;

  const claimsTrend = useMemo(
    () => (query.data?.daily ?? []).map((d) => ({ value: d.claims })),
    [query.data?.daily],
  );
  const filteredRows = useMemo(() => {
    const rows = query.data?.referrals ?? [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        r.code.toLowerCase().includes(q) ||
        (r.owner_name ?? '').toLowerCase().includes(q) ||
        (r.owner_phone ?? '').toLowerCase().includes(q),
    );
  }, [query.data?.referrals, search]);

  const columns = useMemo<ColumnDef<ReferralRow, unknown>[]>(
    () => [
      {
        id: 'code',
        header: 'Code',
        cell: ({ row }) => (
          <div>
            <div className="font-mono text-xs font-semibold uppercase tracking-wider">{row.original.code}</div>
            <div className="text-[11px] text-muted-foreground">
              {row.original.owner_name || row.original.owner_phone || 'No owner'}
            </div>
          </div>
        ),
      },
      { id: 'claims', header: () => <div className="text-right">Claims</div>, cell: ({ row }) => <Num value={row.original.claims} /> },
      { id: 'converted', header: () => <div className="text-right">Converted</div>, cell: ({ row }) => <Num value={row.original.converted_users} /> },
      { id: 'revenue', header: () => <div className="text-right">Revenue / CLV</div>, cell: ({ row }) => <Money value={row.original.clv} /> },
      { id: 'cost', header: () => <div className="text-right">Est. incentive</div>, cell: ({ row }) => <Money value={row.original.estimated_incentive_cost} tone="warning" /> },
      { id: 'cac', header: () => <div className="text-right">Incentive CAC</div>, cell: ({ row }) => <Money value={row.original.incentive_cac} /> },
      {
        id: 'payback',
        header: () => <div className="text-right">Payback</div>,
        cell: ({ row }) => (
          <div className="text-right">{row.original.payback == null ? '—' : `${row.original.payback.toFixed(2)}x`}</div>
        ),
      },
      {
        id: 'conversion',
        header: () => <div className="text-right">Conversion</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <Badge tone={row.original.conversion_rate > 0.25 ? 'success' : 'neutral'}>
              {fmt.pct(row.original.conversion_rate)}
            </Badge>
          </div>
        ),
      },
    ],
    [],
  );

  const totals = query.data?.totals;
  return (
    <>
      {showKpis ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <KPICard label="Claims" value={fmt.number(totals?.claims ?? 0)} loading={query.isLoading} trend={claimsTrend} />
          <KPICard label="Converted users" value={fmt.number(totals?.converted_users ?? 0)} loading={query.isLoading} />
          <KPICard label="Referral revenue" value={fmt.npr(totals?.referred_revenue ?? 0)} loading={query.isLoading} />
          <KPICard
            label="Est. incentive CAC"
            value={fmt.npr(totals?.incentive_cac ?? null)}
            goodWhenUp={false}
            loading={query.isLoading}
            hint={`Estimated from used benefits × ${fmt.npr(totals?.delivery_fee_basis ?? 0)} delivery fee basis.`}
          />
          <KPICard label="Avg CLV" value={fmt.npr(totals?.avg_clv ?? 0)} loading={query.isLoading} />
        </div>
      ) : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-semibold">Referral catalog</h3>
              <Badge tone="info" className="gap-1">
                <Gift className="h-3 w-3" /> {fmt.number(totals?.active_codes ?? 0)} active
              </Badge>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-center">
              <Stat label="Codes used" value={fmt.number(totals?.unique_codes ?? 0)} />
              <Stat label="Referred users" value={fmt.number(totals?.referred_users ?? 0)} />
            </div>
          </CardBody>
        </Card>
        <Card className="lg:col-span-2">
          <CardBody>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-display text-base font-semibold">Conversion and benefit usage</h3>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Conversion" value={fmt.pct(totals?.conversion_rate ?? 0)} />
              <Stat label="Repeat rate" value={fmt.pct(totals?.repeat_rate ?? 0)} />
              <Stat label="Benefits used" value={fmt.number(totals?.used_benefits ?? 0)} />
              <Stat label="Payback" value={totals?.payback == null ? '—' : `${totals.payback.toFixed(2)}x`} />
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState description="Couldn't load referral performance." onRetry={() => void query.refetch()} />
        ) : (
          <DataView<ReferralRow>
            title="Referral codes"
            description="Click a row to drill into referred customers, orders, and stores."
            data={filteredRows}
            columns={columns}
            isLoading={query.isLoading}
            onRowClick={(row) => setSelectedId(row.refer_code_id)}
            search={{ value: search, onChange: setSearch, placeholder: 'Search code, owner, or phone…' }}
            emptyTitle="No referral claims in this window"
          />
        )}
      </div>

      <ReferralDetailSection
        selectedReferral={selectedReferral}
        detail={detail.data}
        detailReturnTo={detailReturnTo}
        isLoading={detail.isLoading}
        isError={detail.isError}
        onRetry={() => void detail.refetch()}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}

function ReferralDetailSection({
  selectedReferral,
  detail,
  detailReturnTo,
  isLoading,
  isError,
  onRetry,
  onClose,
}: {
  selectedReferral: ReferralRow | null;
  detail: ReturnType<typeof useReferralDetail>['data'];
  detailReturnTo: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onClose: () => void;
}) {
  const pageSize = 20;
  const [customerPage, setCustomerPage] = useState(1);
  const [orderPage, setOrderPage] = useState(1);
  const [storePage, setStorePage] = useState(1);
  useEffect(() => {
    setCustomerPage(1);
    setOrderPage(1);
    setStorePage(1);
  }, [selectedReferral?.refer_code_id]);

  const customerColumns = useMemo<ColumnDef<ReferralDetailCustomerRow, unknown>[]>(
    () => [
      {
        id: 'customer',
        accessorFn: (row) => row.name || row.phone || `#${row.user_id}`,
        header: 'Customer',
        cell: ({ row }) => (
          <div>
            <DrillLink to={`/customers/${row.original.user_id}`} from={detailReturnTo}>
              #{row.original.user_id}
            </DrillLink>
            <div className="text-[11px] text-muted-foreground">
              {row.original.name || row.original.phone || 'Customer'}
            </div>
          </div>
        ),
      },
      { accessorKey: 'claimed_at', header: 'Claimed', cell: ({ row }) => fmt.datetime(row.original.claimed_at) },
      { accessorKey: 'used_benefits', header: () => <div className="text-right">Benefits</div>, cell: ({ row }) => <Num value={row.original.used_benefits} /> },
      { accessorKey: 'orders', header: () => <div className="text-right">Orders</div>, cell: ({ row }) => <Num value={row.original.orders} /> },
      { accessorKey: 'clv', header: () => <div className="text-right">CLV</div>, cell: ({ row }) => <Money value={row.original.clv} /> },
    ],
    [detailReturnTo],
  );
  const orderColumns = useMemo<ColumnDef<GrowthOrderDrillRow, unknown>[]>(
    () => [
      {
        accessorKey: 'order_id',
        header: 'Order',
        cell: ({ row }) => (
          <DrillLink to={`/orders/${row.original.order_id}`} from={detailReturnTo}>
            #{row.original.order_id}
          </DrillLink>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) =>
          row.original.customer_id == null ? (
            '—'
          ) : (
            <div>
              <DrillLink to={`/customers/${row.original.customer_id}`} from={detailReturnTo}>
                #{row.original.customer_id}
              </DrillLink>
              {row.original.customer_name ? (
                <div className="text-[11px] text-muted-foreground">{row.original.customer_name}</div>
              ) : null}
            </div>
          ),
      },
      { accessorKey: 'store_name', header: 'Store', cell: ({ row }) => row.original.store_name ?? '—' },
      { accessorKey: 'final_total', header: () => <div className="text-right">Final total</div>, cell: ({ row }) => <Money value={row.original.final_total} /> },
      { accessorKey: 'completed_timestamp', header: 'Completed', cell: ({ row }) => fmt.datetime(row.original.completed_timestamp) },
    ],
    [detailReturnTo],
  );
  const storeColumns = useMemo<ColumnDef<GrowthStoreDrillRow, unknown>[]>(
    () => [
      { accessorKey: 'store_name', header: 'Store', cell: ({ row }) => row.original.store_name ?? '—' },
      { accessorKey: 'orders', header: () => <div className="text-right">Orders</div>, cell: ({ row }) => <Num value={row.original.orders} /> },
      { accessorKey: 'revenue', header: () => <div className="text-right">Revenue</div>, cell: ({ row }) => <Money value={row.original.revenue} /> },
      { accessorKey: 'avg_aov', header: () => <div className="text-right">AOV</div>, cell: ({ row }) => <Money value={row.original.avg_aov} /> },
    ],
    [],
  );

  if (!selectedReferral) return null;
  return (
    <Card className="mt-4">
      <CardHeader>
        <div>
          <CardTitle>Drill-down</CardTitle>
          <div className="mt-1 flex items-center gap-2">
            <Badge tone="primary" className="font-mono uppercase">{selectedReferral.code}</Badge>
            <span className="text-xs text-muted-foreground">
              {detail?.totals.referred_users ?? selectedReferral.referred_users} referred users
            </span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
      </CardHeader>
      <CardBody>
        {isError ? (
          <ErrorState description="Couldn't load referral drill-down." onRetry={onRetry} />
        ) : (
          <div className="grid gap-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Orders" value={fmt.number(detail?.totals.orders ?? 0)} />
              <Stat label="Revenue" value={fmt.npr(detail?.totals.revenue ?? 0)} />
              <Stat label="Est. incentive" value={fmt.npr(detail?.totals.estimated_incentive_cost ?? 0)} />
              <Stat label="Payback" value={detail?.totals.payback == null ? '—' : `${detail.totals.payback.toFixed(2)}x`} />
            </div>
            <div className="grid gap-4 xl:grid-cols-3">
              <DataView
                title="Customers"
                data={detail?.customers ?? []}
                columns={customerColumns}
                isLoading={isLoading}
                clientPagination
                pagination={{
                  page: customerPage,
                  pageSize,
                  total: detail?.customers.length ?? 0,
                  onPageChange: setCustomerPage,
                }}
                density="compact"
                emptyTitle="No customers"
              />
              <DataView
                title="Orders"
                data={detail?.orders ?? []}
                columns={orderColumns}
                isLoading={isLoading}
                clientPagination
                pagination={{
                  page: orderPage,
                  pageSize,
                  total: detail?.orders.length ?? 0,
                  onPageChange: setOrderPage,
                }}
                density="compact"
                emptyTitle="No orders"
              />
              <DataView
                title="Stores"
                data={detail?.stores ?? []}
                columns={storeColumns}
                isLoading={isLoading}
                clientPagination
                pagination={{
                  page: storePage,
                  pageSize,
                  total: detail?.stores.length ?? 0,
                  onPageChange: setStorePage,
                }}
                density="compact"
                emptyTitle="No stores"
              />
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface px-3 py-2">
      <div className="kpi-num text-lg font-semibold">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function Num({ value }: { value: number | null | undefined }) {
  return <div className="kpi-num text-right text-sm">{fmt.number(value ?? 0)}</div>;
}

function Money({ value, tone }: { value: number | null | undefined; tone?: 'warning' }) {
  return (
    <div className={cn('kpi-num text-right text-sm', tone === 'warning' && 'text-warning')}>
      {value == null ? '—' : fmt.npr(value)}
    </div>
  );
}

function DrillLink({ to, from, children }: { to: string; from: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      state={{ from }}
      className="font-mono text-xs font-semibold text-primary hover:underline"
    >
      {children}
    </Link>
  );
}
